import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input, Button, CheckBox, VDBText, Spacer } from "@components/atoms";
import { ButtonType, ButtonCategory } from "@components/atoms/Button/types";

import { VDBIcons } from "@utils/VDBIcons";
import { useTranslation } from "react-i18next";
import { schema } from "../loginSchema";
import { TouchableOpacity } from "react-native";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { getFromSecureStore } from "@utils/index";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { useDispatch } from "react-redux";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import { LoginFormData } from "@src/types/UserActivation";
import { AuthFormProps } from "../Types/types";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import { useAutoEnterKeySubmit } from "../../../../hooks/useAutoEnterKeySubmit";

export const AuthForm: React.FC<AuthFormProps> = ({
  onSubmit,
  isLoading,
  rememberMeChecked,
  setUsername,
  setUsernameFormValue,
  setFormPassword,
  setRememberMeChecked,
}) => {
  const { t } = useTranslation();
  const [enableLogin, setEnableLogin] = useState(false);
  const [userName, setUserName] = useState<string>("");

  const dispatch = useDispatch();
  const { control, handleSubmit, getValues, setValue } = useForm<LoginFormData>(
    {
      resolver: yupResolver(schema(t)),
    }
  );
  const { theme, typography } = useScreenOptionsHooks();

  const userInfo = useUserInfo();

  useAutoEnterKeySubmit(handleSubmit(onSubmit), !enableLogin || isLoading);

  const handleForgotPassword = () => {
    if (userInfo.isGuest) {
      navigate(Routes.GlobalNavigator, {
        headerShown: "true",
        screen: Routes.ForgotPassword,
        params: {
          username: userName,
          fromGlobal: true,
        },
      });
      dispatch(setLoginModalType("none"));
    } else {
      navigate(Routes.ForgotPassword, { username: userName });
    }
  };

  useEffect(() => {
    const getData = async () => {
      const rememberMe = await getFromSecureStore("remember_me");
      if (rememberMe === "true") {
        const storeUsername = await getFromSecureStore("username");
        const storePassword = await getFromSecureStore("password");
        setEnableLogin(!!storeUsername && !!storePassword);

        setRememberMeChecked(true);
        if (!!storeUsername && !!storePassword) {
          setValue("username", storeUsername);
          setValue("password", storePassword);
          setUserName(storeUsername);
          setUsername(storeUsername);
          setUsernameFormValue(storeUsername);
          setFormPassword(storePassword);
        }
      } else if (rememberMe === "false") {
        setRememberMeChecked(false);
      } else {
        setRememberMeChecked(true);
      }
    };
    getData().then();
  }, []);

  const checkEnableLogin = (user: string, pass: string) => {
    setEnableLogin(!!user && !!pass);
  };

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  return (
    <FormContainer>
      <Controller
        control={control}
        render={({
          field: { onChange, onBlur, value },
          formState: { errors },
        }) => (
          <Input
            inputRef={usernameRef}
            inputLabel={t("login.input.usename.placeholderText")}
            leftIcon={VDBIcons.PROFILE}
            autoCapitalize="none"
            placeholder={t("login.input.usename.placeholderText")}
            hideIconContainer={false}
            onBlur={onBlur}
            showAccessoryPrevNext
            onAccessoryNext={() => passwordRef.current?.focus()}
            returnKeyType="next"
            onSubmitEditing={() => passwordRef.current?.focus()}
            onChangeText={(text) => {
              onChange(text);
              checkEnableLogin(text, getValues("password"));
              setUserName(getValues("username"));
              setUsername(getValues("username"));
              setUsernameFormValue(getValues("username"));
            }}
            value={value ?? ""}
            error={errors?.username?.message}
          />
        )}
        name="username"
      />

      <Spacer size={SpaceType.Big} />

      <Controller
        control={control}
        render={({
          field: { onChange, onBlur, value },
          formState: { errors },
        }) => (
          <Input
            inputRef={passwordRef}
            inputLabel={t("login.input.password.placeholderText")}
            leftIcon={VDBIcons.KEY_SQUARE}
            placeholder={t("login.input.password.placeholderText")}
            hideIconContainer={false}
            secureTextEntry={true}
            onBlur={onBlur}
            showAccessoryPrevNext
            onAccessoryPrev={() => usernameRef.current?.focus()}
            returnKeyType="done"
            onChangeText={(text) => {
              onChange(text);
              checkEnableLogin(getValues("username"), text);
            }}
            value={value ?? ""}
            error={errors?.password?.message}
          />
        )}
        name="password"
      />

      <Spacer size={SpaceType.Big} />

      <CheckboxContainer>
        <CheckBox
          text={t("login.linkButton.rememberMe.text")}
          isChecked={rememberMeChecked}
          onPress={(checked: boolean) => {
            setRememberMeChecked(checked);
          }}
          labelColor={rememberMeChecked ? theme["core-05"] : theme["core-06"]}
          style={
            rememberMeChecked ? typography["Body-M"] : typography["Body-R"]
          }
        />
        <TouchableOpacity onPress={handleForgotPassword}>
          <VDBText color={theme["link-static"]} style={typography["Body-M"]}>
            {t("login.linkButton.forgotPassword.text")}
          </VDBText>
        </TouchableOpacity>
      </CheckboxContainer>

      <Spacer size={SpaceType.ExtraLarge} />
      <Button
        loading={isLoading}
        type={ButtonType.primary}
        disabled={!enableLogin || isLoading}
        category={ButtonCategory.default}
        title={t("login.button.login.text")}
        onPress={handleSubmit(onSubmit)}
        textTypographyStyle="H3-M"
      />
    </FormContainer>
  );
};

const FormContainer = styled.View`
  width: 100%;
`;

const CheckboxContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
